import { useState, useEffect } from 'react';
import inventoryManagementService from 'services/inventory-items-service';

// MUI and Material Dashboard Components
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MDBox from "components/MDBox";
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

export default function EditReservationDialog({
    handleClose,
    open,
    reservationId
}) {
    const [quantity, setQuantity] = useState(0);
    const [comment, setComment] = useState('');
    const [errors, setErrors] = useState({});
    const [openSnackbar, setOpenSnackbar] = useState(false);

    // Fetch reservation details when dialog opens
    useEffect(() => {
        if (open && reservationId) {
            getReservationDetails();
        }
    }, [open, reservationId]);

    const getReservationDetails = async () => {
        try {
            const response = await inventoryManagementService.getReservationById(reservationId);
            if (response && response.data && response.data.attributes) {
                setQuantity(response.data.attributes.qty);
                setComment(response.data.attributes.comment || '');
            }
        } catch (error) {
            console.error("Error fetching reservation details:", error);
            setErrors({ submit: "Failed to load reservation details." });
            setOpenSnackbar(true);
        }
    };

    // Validate form fields
    const checkErrors = () => {
        let tempErrors = {};

        if (quantity <= 0) {
            tempErrors.quantity = "Quantity must be greater than zero.";
        }
        if (comment.trim() === '') {
            tempErrors.comment = "Comment is required for reservations.";
        }

        setErrors(tempErrors);
        return Object.keys(tempErrors).length === 0;
    }

    // Handle Close Snackbar
    const handleCloseSnackbar = () => {
        setOpenSnackbar(false);
        // We generally keep field errors until corrected, but verify if you want to clear them here
    }

    // Handle Save button click
    const onClickSave = async () => {
        try {
            setErrors({});
            if (!checkErrors()) {
                return;
            }

            const payload = {
                data: {
                    type: "reservations",
                    id: reservationId,
                    attributes: {
                        quantity: parseInt(quantity),
                        comment: comment
                    }
                }
            };

            await inventoryManagementService.editReservation(reservationId, payload);

            // On success
            setQuantity(0);
            setComment('');
            handleClose(parseInt(quantity), comment); // Pass updated data back to parent
        } catch (error) {
            console.error("Error editing reservation:", error);
            setErrors({ submit: error.error || 'Failed to update reservation. Please try again.' });
            setOpenSnackbar(true);
        }
    }

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                width="30vh"
            >
                {/* Snackbar for server-side errors */}
                <Snackbar
                    open={openSnackbar}
                    autoHideDuration={3000}
                    onClose={handleCloseSnackbar}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseSnackbar}
                        severity="error"
                        sx={{ width: '100%', alignItems: 'center' }}
                    >
                        {errors.submit}
                    </Alert>
                </Snackbar>

                {/* Dialog Title */}
                <DialogTitle>Edit Reservation</DialogTitle>

                <DialogContent>
                    <MDBox width="400px" display="flex" flexDirection="column">
                        {/* Quantity Input Field */}
                        <MDBox mt={2}>
                            <MDInput
                                label="Quantity"
                                type="number"
                                value={quantity}
                                onChange={(e) => setQuantity(e.target.value)}
                                error={!!errors.quantity}
                                helperText={errors.quantity}
                                fullWidth
                            />
                        </MDBox>

                        {/* Comment Input Field */}
                        <MDBox mt={2}>
                            <MDInput
                                label="Comment"
                                type="text"
                                multiline
                                rows={3}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                error={!!errors.comment}
                                helperText={errors.comment}
                                fullWidth
                            />
                        </MDBox>
                    </MDBox>
                </DialogContent>

                {/* Dialog Actions */}
                <DialogActions fullWidth>
                    <MDButton
                        onClick={handleClose}
                        color="secondary"
                        variant="outlined"
                        size="small"
                        fullWidth
                    >
                        Cancel
                    </MDButton>
                    <MDButton
                        onClick={onClickSave}
                        color="info"
                        variant="outlined"
                        size="small"
                        fullWidth
                    >
                        Save
                    </MDButton>
                </DialogActions>
            </Dialog>
        </>
    )
}